import { Users } from 'lucide-react';
import { formatRange } from './utils';
import { formatMinor, TaxEmptyState, TaxReportCard, TaxStatCell, TaxStatStrip } from './taxReportShared';

interface ContractorItem {
  contractorId: string;
  legalName: string;
  tin: string | null;
  totalPaidMinor: number;
  exceedsThreshold: boolean;
  hasW9: boolean;
}

interface Data {
  type: string;
  periodStart: string;
  periodEnd: string;
  thresholdMinor: number;
  contractors: ContractorItem[];
  totalContractors: number;
  exceedsThresholdCount: number;
}

export function Tax1099SummaryReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const contractors = d.contractors ?? [];

  return (
    <TaxReportCard
      icon={Users}
      title="1099 Summary"
      badge={formatRange(d.periodStart, d.periodEnd)}
    >
      <TaxStatStrip>
        <TaxStatCell label="Above threshold" value={d.exceedsThresholdCount} />
        <TaxStatCell label="Threshold" value={formatMinor(d.thresholdMinor)} />
        <TaxStatCell label="Contractors" value={d.totalContractors} />
      </TaxStatStrip>

      {contractors.length === 0 ? (
        <TaxEmptyState message="No 1099-eligible contractor payments for this period." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-50/60 text-[11px] uppercase tracking-[0.06em] text-surface-500">
                <th className="px-4.5 py-2 text-left font-medium">Contractor</th>
                <th className="px-3 py-2 text-left font-medium">TIN</th>
                <th className="px-3 py-2 text-center font-medium">W-9</th>
                <th className="px-3 py-2 text-right font-medium">Total paid</th>
                <th className="px-4.5 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {contractors.map((c) => (
                <tr
                  key={c.contractorId}
                  className="border-t border-surface-100 transition-colors hover:bg-surface-50"
                >
                  <td className="px-4.5 py-1.75 text-[13px] text-surface-700">{c.legalName}</td>
                  <td className="px-3 py-1.75 font-mono text-[12px] tabular-nums text-surface-500">
                    {c.tin ?? '—'}
                  </td>
                  <td className="px-3 py-1.75 text-center text-[12px]">
                    {c.hasW9 ? (
                      <span className="text-pos">✓</span>
                    ) : (
                      <span className="text-surface-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.75 text-right font-mono text-[13px] font-semibold tabular-nums text-surface-900">
                    {formatMinor(c.totalPaidMinor)}
                  </td>
                  <td className="px-4.5 py-1.75">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        c.exceedsThreshold
                          ? 'bg-warn-surface text-warn'
                          : 'bg-surface-50 text-surface-500'
                      }`}
                    >
                      {c.exceedsThreshold ? '1099 required' : 'Below threshold'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TaxReportCard>
  );
}
